<template>
  <div class="flex flex-col gap-6" data-testid="page-manage-orders">
    <h2 class="txt-headline-2xl-bold">{{ $t('FestivalShop.orders.manage.title') }}</h2>

    <div v-if="!ordersWithProducts.length">
      <div class="mb-6 flex flex-col">
        <strong>{{ $t('FestivalShop.orders.manage.no_order_callout') }}</strong>
        {{ $t('FestivalShop.orders.manage.no_order_callout_text') }}
      </div>
      <div class="grid w-full grid-cols-2 gap-3">
        <CategoryCard v-for="cat in productCategories" :key="cat.id" :category="cat" />
      </div>
    </div>
    <div v-else>
      <div v-for="order in ordersWithProducts" :key="order.id">
        <div class="mb-6 flex flex-row-reverse rounded-lg border border-main bg-surface">
          <div class="flex flex-grow flex-col gap-4 px-6 py-4">
            <OrderItemLister
              v-for="cat in filteredCategories(order)"
              :key="cat.id"
              :category="cat"
              :items="itemsInCategory(order, cat.id)"
              :order-id="order.id"
              :event-slug="rootEventSlug"
              :kiosk-id="kiosk.id"
              @change-delivery-time="handleDeliveryTimeChange"
            />
            <OrderItemLister
              v-if="itemsWithoutCategory(order).length"
              :category="null"
              :items="itemsWithoutCategory(order)"
              :order-id="order.id"
              :event-slug="rootEventSlug"
              :kiosk-id="kiosk.id"
              @change-delivery-time="handleDeliveryTimeChange"
            />
          </div>
          <div class="flex w-[250px] shrink-0 flex-col gap-3 border-r border-r-subtle p-6">
            <OrderSummary :order="order" :event-slug="rootEventSlug" :kiosk-id="kiosk.id" />
          </div>
        </div>
      </div>
      <ChangeDeliveryTimeModalVue
        v-if="selectedCategoryForDeliveryTimeChange"
        :category="selectedCategoryForDeliveryTimeChange"
        :kiosk-id="kiosk.id"
        @close="selectedCategoryForDeliveryTimeChange = null"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue'

import CategoryCard from '@/components/FestivalShop/CategoryCard.vue'
import { type EventBySlugQuery, type KioskUnion, type ProductCategory } from '@/gql/myomr'

import ChangeDeliveryTimeModalVue from './Orders/ChangeDeliveryTimeModalVue.vue'
import OrderItemLister from './Orders/OrderItemLister.vue'
import OrderSummary from './Orders/OrderSummary.vue'

import { getOrderDisplayItems, type ExtrasOrderItem, type ExtrasOrderNode } from '.'

const props = defineProps<{
  event: NonNullable<EventBySlugQuery['eventBySlug']> & { slug: string }
  kiosk: KioskUnion
  extrasOrders: ExtrasOrderNode[]
  productCategories: ProductCategory[]
}>()

const selectedCategoryForDeliveryTimeChange = ref<ProductCategory | null>(null)
const rootEventSlug = computed(() => props.event.rootEvent?.slug ?? props.event.slug ?? '')

const filteredCategories = computed(() => {
  return (o: ExtrasOrderNode) => {
    return props.productCategories.filter((c) => itemsInCategory(o, c.id).length > 0).sort((a, b) => a.position - b.position)
  }
})

const ordersWithProducts = computed(() => {
  return props.extrasOrders.filter((order) => getOrderDisplayItems(order).length > 0)
})

const itemsInCategory = (order: ExtrasOrderNode, catId: string): ExtrasOrderItem[] => {
  const extras = getOrderDisplayItems(order)
  return extras.filter((extra) => extra.product.productCategory?.id === catId)
}

const itemsWithoutCategory = (order: ExtrasOrderNode): ExtrasOrderItem[] => {
  const extras = getOrderDisplayItems(order)
  const categoryIds = new Set(props.productCategories.map((category) => category.id))
  return extras.filter((extra) => !extra.product.productCategory?.id || !categoryIds.has(extra.product.productCategory.id))
}

const handleDeliveryTimeChange = (category: ProductCategory) => {
  selectedCategoryForDeliveryTimeChange.value = category
}
</script>
